<template>
    <Layout is-crm class="customers-default-speakers-page" :content-class="'content--centered'">
        <header class="page__top">
            <h1 v-t="'crm.defaultSpeakers.title'"></h1>
            <div class="customers-default-speakers-page__toolbar">
                <WithGlobalPermission permission="CustomerDefaultSpeaker.create">
                    <CustomerDefaultSpeakersModal
                        v-if="schema && bookLanguages"
                        :schema="schema.create"
                        :customer="customerId"
                        :book-languages="bookLanguages"
                        @saved="refetch"
                    />
                </WithGlobalPermission>
            </div>
        </header>
        <PaginatedResource
            ref="speakersRes"
            :fetch-method="fetchCustomersDefaultSpeakers"
            :filter-fields="filtersFields"
            :sorting-fields="sortingFields"
        >
            <template
                #default="{ loading, canLoadMore, resource: items, isEmpty, notFound, loadMore }"
            >
                <div class="customers-default-speakers-page__results">
                    <Spinner v-if="loading" />
                    <table class="control-table">
                        <thead class="control-table__head">
                            <tr class="control-table__row">
                                <th class="control-table__cell" style="width: 150px">
                                    <div class="control-table__cell-inner">
                                        <span v-text="'ID'" />
                                        <TableSorting v-model="ordering" field="id" />
                                    </div>
                                </th>
                                <th class="control-table__cell">
                                    <div class="control-table__cell-inner">
                                        <span v-t="'misc.language'" />
                                        <TableSorting v-model="ordering" field="language" />
                                    </div>
                                </th>
                                <th class="control-table__cell">
                                    <div class="control-table__cell-inner">
                                        <span v-t="'misc.voice'" />
                                        <TableSorting v-model="ordering" field="speaker" />
                                    </div>
                                </th>
                                <th class="control-table__cell" style="width: 150px">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="!loading">
                                <tr v-if="isEmpty" class="control-table__row">
                                    <td
                                        class="table__cell table__cell--centered table__empty"
                                        colspan="4"
                                    >
                                        {{ $('crm.defaultSpeakers.results.noData') }}
                                    </td>
                                </tr>
                                <tr v-else-if="notFound" class="control-table__row">
                                    <td
                                        class="table__cell table__cell--centered table__empty"
                                        colspan="4"
                                    >
                                        {{ $t('crm.defaultSpeakers.results.noResults') }}
                                    </td>
                                </tr>
                                <tr v-for="item in items" :key="item.id" class="control-table__row">
                                    <td class="control-table__cell">
                                        <span v-text="item.id" />
                                    </td>
                                    <td class="control-table__cell">
                                        <span v-text="item.language.label" />
                                    </td>
                                    <td class="control-table__cell">
                                        <span v-text="item.speaker.name[$i18n.locale]" />
                                    </td>
                                    <td class="control-table__cell">
                                        <div class="customers-default-speakers-page__actions">
                                            <CustomerDefaultSpeakersModal
                                                v-if="
                                                    item.permissions.update &&
                                                    schema &&
                                                    bookLanguages
                                                "
                                                :schema="schema.create"
                                                :customer="customerId"
                                                :book-languages="bookLanguages"
                                                :item="{
                                                    id: item.id,
                                                    language: item.language.slug,
                                                    speaker: item.speaker.id,
                                                }"
                                                @saved="refetch"
                                            >
                                                <template #trigger="{ open }">
                                                    <button
                                                        type="button"
                                                        class="button button--icon"
                                                        @click="open"
                                                    >
                                                        <IconPencil />
                                                    </button>
                                                </template>
                                            </CustomerDefaultSpeakersModal>
                                            <CustomerDefaultSpeakersDelete
                                                v-if="item.permissions.destroy"
                                                :item-id="item.id"
                                                @deleted="refetch"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <div v-if="canLoadMore" class="customers-default-speakers-page__loadmore">
                        <button
                            v-t="'misc.loadmore'"
                            class="button button--big button--blank"
                            type="button"
                            @click="loadMore"
                        />
                    </div>
                </div>
            </template>
        </PaginatedResource>
    </Layout>
</template>

<script>
import {
    fetchCustomersDefaultSpeakers,
    fetchCustomersDefaultSpeakersSchema,
} from '../../api/customers';
import { fetchBookLanguages } from '@speechki-frontend/core/book/api/books';
import { CustomerDefaultSpeakersModal, CustomerDefaultSpeakersDelete } from '../../components';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import TableSorting from '@speechki-frontend/shared/ui/TableSorting';
import { IconPencil } from '@speechki-frontend/shared/ui/icons';
import { WithGlobalPermission } from '@speechki-frontend/core/auth/components';

export default {
    name: 'CustomerDefaultSpeakersPage',
    components: {
        Layout,
        CustomerDefaultSpeakersModal,
        CustomerDefaultSpeakersDelete,
        PaginatedResource,
        TableSorting,
        IconPencil,
        WithGlobalPermission,
    },
    data() {
        return {
            schema: undefined,
            bookLanguages: undefined,
            sortingFields: ['id', 'language', 'speaker'],
            ordering: '-id',
        };
    },
    computed: {
        customerId() {
            return this.$route.params.id;
        },
        filtersFields() {
            return {
                customer: this.customerId,
            };
        },
    },
    watch: {
        ordering(value) {
            this.$refs.speakersRes.sort(value);
        },
    },
    async created() {
        try {
            await Promise.all([this.fetchSchema(), this.fetchBookLanguages()]);
        } catch (err) {
            console.log(err);
        }
    },
    methods: {
        fetchCustomersDefaultSpeakers,

        async fetchSchema() {
            this.schema = await fetchCustomersDefaultSpeakersSchema();
        },
        async fetchBookLanguages() {
            const bookLanguages = await fetchBookLanguages();
            this.bookLanguages = Object.entries(bookLanguages).map(([key, value]) => ({
                id: key,
                name: value,
            }));
        },
        refetch() {
            this.$refs.speakersRes.fetch();
        },
    },
};
</script>

<style lang="scss" src="./CustomerDefaultSpeakersPage.scss"></style>
